import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function CTA() {
  const { ref, inView } = useInView(0.15);

  return (
    <section className="relative py-40 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=yellow%20taxi%20cab%20driving%20fast%20istanbul%20night%20city%20lights%20bokeh%20motion%20blur%20cinematic%20professional%20photography%20dark%20moody%20atmosphere%20turkey%20bosphorus&width=1920&height=800&seq=cta-taxi-night-v2&orientation=landscape"
          alt="Başharık Taksi Malatya - 7/24 Taksi Hizmeti ve Acil Çağrı"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/80" />
      </div>

      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
      <div className="absolute top-1/2 left-0 w-32 h-1 bg-yellow-400 transform -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-32 h-1 bg-yellow-400 transform -translate-y-1/2" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-8 text-center">
        <div
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(-30px)',
            transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0s',
          }}
          className="inline-flex items-center space-x-2 bg-yellow-400 px-6 py-3 rounded-full mb-10 shadow-2xl"
        >
          <i className="ri-time-line text-black text-lg" style={{ animation: inView ? 'spinOnce 0.6s ease 0.5s both' : 'none' }} />
          <span className="text-black text-xs font-black uppercase tracking-widest whitespace-nowrap">7/24 Hizmet</span>
        </div>

        <h2
          className="text-7xl lg:text-9xl font-black text-white mb-8 leading-tight uppercase tracking-tight"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16,1,0.3,1) 0.15s',
          }}
        >
          Hemen Arayın,<br />
          <span
            className="text-yellow-400 relative inline-block"
            style={{ textShadow: inView ? '0 0 80px rgba(250,204,21,0.6)' : 'none', transition: 'text-shadow 1s ease 0.5s' }}
          >
            Hemen Gelelim!
            <div className="absolute -bottom-4 left-0 w-full h-2 bg-yellow-400/30" />
          </span>
        </h2>

        <p
          className="text-2xl text-white/70 mb-16 max-w-2xl mx-auto font-semibold"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.3s',
          }}
        >
          Güvenli ve konforlu yolculuk için tek yapmanız gereken bizi aramak.
        </p>

        <div
          className="flex flex-col items-center space-y-6"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
            transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1) 0.45s',
          }}
        >
          <a
            href="tel:05308920944"
            className="inline-flex items-center space-x-5 bg-yellow-400 hover:bg-yellow-300 text-black px-16 py-8 rounded-2xl font-black text-3xl transition-all duration-300 cursor-pointer whitespace-nowrap shadow-2xl hover:shadow-yellow-400/50 hover:scale-105 group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <i className="ri-phone-fill text-4xl relative z-10" style={{ animation: 'phoneBounce 1.5s ease-in-out infinite' }} />
            <span className="relative z-10">0530 892 09 44</span>
          </a>

          <div className="flex items-center space-x-8">
            <div className="w-24 h-0.5 bg-yellow-400/50" />
            <span className="text-white/50 text-sm font-bold uppercase tracking-widest whitespace-nowrap">veya</span>
            <div className="w-24 h-0.5 bg-yellow-400/50" />
          </div>

          <a
            href="https://wa.me/905302064644"
            className="inline-flex items-center space-x-3 bg-green-500 hover:bg-green-400 text-white px-12 py-5 rounded-2xl transition-all duration-300 cursor-pointer whitespace-nowrap text-lg font-black shadow-xl hover:shadow-green-500/50 hover:scale-105"
          >
            <i className="ri-whatsapp-line text-3xl" />
            <span>WhatsApp ile Ulaşın</span>
          </a>
        </div>

        <div className="mt-16 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
          {[
            { icon: 'ri-time-line', label: '7/24 Hizmet' },
            { icon: 'ri-shield-check-line', label: 'Güvenli Yolculuk' },
            { icon: 'ri-money-dollar-circle-line', label: 'Uygun Fiyat' },
          ].map((item, i) => (
            <div
              key={i}
              className="text-center"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.8)',
                transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${0.6 + i * 0.15}s`,
              }}
            >
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3 hover:scale-110 transition-transform duration-300 cursor-default">
                <i className={`${item.icon} text-black text-2xl`} />
              </div>
              <p className="text-white font-black text-sm whitespace-nowrap">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes phoneBounce {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-15deg); }
          40% { transform: rotate(15deg); }
          60% { transform: rotate(-10deg); }
          80% { transform: rotate(10deg); }
        }
        @keyframes spinOnce {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
}
